import NapsterSpinner from '@components/NapsterSpinner';
import { useCommonState } from '@hooks/storeHooks/useCommonState';
import { useMusicState } from '@hooks/storeHooks/useMusicState';
import useAlbumData from '@hooks/useAlbumData';
import { memo, useCallback, useEffect, useState } from 'react';
import { placeHolderData } from '../../config';
import demoLeaderboardData from '../../datas/demoIndianLeaderboard.json';
import apiBase from '../../utils/baseApi';
import { getTrackUrlForLocale } from '../../utils/localeUtils';

// Set to true for static demo data, false for dynamic API data
const USE_STATIC_DATA = true;

interface Track {
  id: string;
  name: string;
  duration: number;
  urls?: Array<{ url: string; locale: string; extension: string }>;
  url?: string;
}

interface Contributor {
  id: string;
  givenName: string;
  thumbnailUrl?: string;
  videoMobileUrl?: string;
  videoDesktopUrl?: string;
}

interface Album {
  id: string;
  name?: string;
  albumName?: string;
  albumArtUrl?: string;
  thumbnailUrl?: string;
  videoMobileUrl?: string;
  videoDesktopUrl?: string;
  videoMobilePreviewUrl?: string;
  videoDesktopPreviewUrl?: string;
  tracks?: Track[];
  contributors?: Contributor[];
}

interface PostedBy {
  name: string;
  avatarUrl: string;
}

interface LeaderboardTrack {
  track: Track;
  album: Album;
  contributor: Contributor;
  playCount: number;
  rank: number;
  postedBy?: PostedBy;
}

const IndianMusicLeaderboardPage = memo(() => {
  const [leaderboardTracks, setLeaderboardTracks] = useState<
    LeaderboardTrack[]
  >([]);
  const [loading, setLoading] = useState(true);
  const { loadAlbumById } = useAlbumData();
  const { setActivePlaylist, addRecentTrack, preferredLocale } =
    useMusicState();
  const { setActivePopup } = useCommonState();
  const [loadingTrackId, setLoadingTrackId] = useState<string | null>(null);

  // Generate random play counts starting at 2M+ going down
  const generatePlayCounts = useCallback((count: number): number[] => {
    const counts: number[] = [];
    let currentCount = 2000000 + Math.floor(Math.random() * 500000); // Start at 2M-2.5M
    for (let i = 0; i < count; i++) {
      counts.push(currentCount);
      // Decrease by random amount (50k-200k) for each subsequent rank
      currentCount -= Math.floor(Math.random() * 150000) + 50000;
    }
    return counts;
  }, []);

  // Load static data from JSON file
  const loadStaticData = useCallback(() => {
    setLoading(true);
    try {
      // Sort by playCount descending
      const sortedData = [...(demoLeaderboardData as LeaderboardTrack[])].sort(
        (a, b) => b.playCount - a.playCount,
      );
      setLeaderboardTracks(sortedData);
    } catch (error) {
      console.error('Error loading static leaderboard data:', error);
      setLeaderboardTracks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch Indian albums and extract random tracks from API
  const fetchLeaderboardFromApi = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch Indian music albums (using category=music with genre=indian music)
      const response = await apiBase.get<{ items: Album[] }>(
        '/api/albums?category=music&limit=12&offset=0&sort=albumName&genre=indian%20music',
      );
      const albums = response.data.items || [];

      if (albums.length === 0) {
        setLeaderboardTracks([]);
        return;
      }

      // Load full album data to get tracks
      const albumsWithTracks: Album[] = [];
      for (const album of albums) {
        const fullAlbum = await loadAlbumById(album.id);
        if (fullAlbum && fullAlbum.tracks && fullAlbum.tracks.length > 0) {
          albumsWithTracks.push(fullAlbum);
        }
      }

      // Collect all tracks from all albums
      const allTracks: { track: Track; album: Album }[] = [];
      for (const album of albumsWithTracks) {
        for (const track of album.tracks || []) {
          allTracks.push({ track, album });
        }
      }

      // Shuffle and pick 12 random tracks
      const shuffled = allTracks.sort(() => Math.random() - 0.5);
      const selected = shuffled.slice(0, 12);

      // Generate play counts
      const playCounts = generatePlayCounts(selected.length);

      // Create leaderboard tracks
      const leaderboard: LeaderboardTrack[] = selected.map((item, index) => ({
        track: item.track,
        album: item.album,
        contributor: item.album.contributors?.[0] || {
          id: '',
          givenName: 'Unknown Artist',
        },
        playCount: playCounts[index],
        rank: index + 1,
      }));

      setLeaderboardTracks(leaderboard);
    } catch (error) {
      console.error('Error fetching Indian music leaderboard:', error);
      setLeaderboardTracks([]);
    } finally {
      setLoading(false);
    }
  }, [loadAlbumById, generatePlayCounts]);

  useEffect(() => {
    if (USE_STATIC_DATA) {
      loadStaticData();
    } else {
      fetchLeaderboardFromApi();
    }
  }, [loadStaticData, fetchLeaderboardFromApi]);

  // Format play count (e.g., 2,345,678)
  const formatPlayCount = (count: number): string => {
    return count.toLocaleString();
  };

  // Handle track click - play the track
  const handleTrackClick = useCallback(
    async (item: LeaderboardTrack) => {
      if (!item.track?.id) return;
      setLoadingTrackId(item.track.id);

      try {
        const trackUrl = getTrackUrlForLocale(item.track, preferredLocale);
        if (trackUrl) {
          const playlist = {
            album: {
              ...item.album,
              category: 'music',
              tracks: [item.track],
            },
            currentIndex: 0,
          };

          const recentTrack = {
            track: {
              name: item.track.name,
              url: trackUrl,
              trackIndex: 1,
            },
            contributor: {
              id: item.contributor.id,
              givenName: item.contributor.givenName,
              thumbnailUrl:
                item.contributor.thumbnailUrl ||
                placeHolderData.contributorThumbnail,
            },
            albumArtUrl:
              item.album.albumArtUrl ||
              item.album.thumbnailUrl ||
              placeHolderData.albumCover,
            timestamp: Date.now(),
          };

          setActivePopup('musicplayer');
          addRecentTrack(recentTrack);
          setActivePlaylist(playlist);
        }
      } catch (error) {
        console.error('Error playing track:', error);
      } finally {
        setLoadingTrackId(null);
      }
    },
    [setActivePlaylist, addRecentTrack, setActivePopup, preferredLocale],
  );
  return (
    <div className="flex flex-col h-full w-full text-white overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 px-4 sm:px-6">
        <div className="flex flex-col">
          <h1 className="text-[32px] sm:text-[40px] font-normal leading-[140%] text-left text-[#fcfcfc] font-['DM_Serif_Text']">
            Leaderboard
          </h1>
          <div className="text-[14px] font-normal leading-[140%] text-left text-white/80 pb-4 font-[NapsterSans]">
            Top 10 tracks this week
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto na-scroll-container px-4 sm:px-6 pb-[125px]">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <NapsterSpinner />
          </div>
        ) : leaderboardTracks.length === 0 ? (
          <div className="flex items-center justify-center h-64 text-white/60">
            No tracks available
          </div>
        ) : (
          <div className="mt-4">
            {/* Table Header */}
            <div className="grid grid-cols-[70px_1fr_1fr_80px] sm:grid-cols-[90px_1fr_1fr_100px] gap-4 px-3 py-3 border-b border-white/10 text-white/60 text-[12px] sm:text-[14px] font-medium items-center">
              <div className="text-left pl-1">Album</div>
              <div className="text-left">Song Title</div>
              <div className="text-left">@User Posted</div>
              <div className="text-right">Listens</div>
            </div>

            {/* Table Rows */}
            <div className="flex flex-col">
              {leaderboardTracks.map((item) => (
                <div
                  key={`${item.track.id}-${item.rank}`}
                  className="grid grid-cols-[70px_1fr_1fr_80px] sm:grid-cols-[90px_1fr_1fr_100px] gap-4 px-3 py-3 items-center hover:bg-white/5 transition-colors cursor-pointer group border-b border-white/5"
                  onClick={() => handleTrackClick(item)}>
                  {/* Album Cover */}
                  <div className="relative w-12 h-12 sm:w-14 sm:h-14 rounded-md overflow-hidden">
                    <img
                      src={
                        item.album.albumArtUrl ||
                        item.album.thumbnailUrl ||
                        placeHolderData.albumCover
                      }
                      alt={item.album.albumName || item.album.name}
                      className="w-full h-full object-cover"
                    />
                    {loadingTrackId === item.track.id && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                        <NapsterSpinner size="small" />
                      </div>
                    )}
                    {/* Play overlay on hover */}
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="white">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  </div>

                  {/* Song Title */}
                  <div className="flex items-center min-w-0 text-left">
                    <h3 className="text-white font-medium text-[14px] sm:text-[16px] truncate">
                      {item.track.name}
                    </h3>
                  </div>

                  {/* @User Posted */}
                  <div className="flex items-center gap-2 min-w-0 text-left">
                    <img
                      src={
                        item.postedBy?.avatarUrl ||
                        placeHolderData.contributorThumbnail
                      }
                      alt={item.postedBy?.name || 'User'}
                      className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex-shrink-0 object-cover"
                    />
                    <span className="text-white/80 text-[12px] sm:text-[14px] truncate">
                      @{item.postedBy?.name || 'Anonymous'}
                    </span>
                  </div>

                  {/* Listens */}
                  <div className="flex items-center justify-end text-right">
                    <span className="text-white/80 text-[12px] sm:text-[14px] font-medium">
                      {formatPlayCount(item.playCount)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

IndianMusicLeaderboardPage.displayName = 'IndianMusicLeaderboardPage';

export default IndianMusicLeaderboardPage;
